import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChildren,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { AdminDashboardService } from '../../../services/admin-dashboard.service';
import { GlobalSettingsService } from '../../../services/global-settings.service';
import { LiveSystemStatus } from './admin-dashboard.models';
import { ChartInstance, sparklineConfig } from './admin-dashboard.chart';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [RouterLink, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminDashboardComponent implements AfterViewInit, OnDestroy {
  private readonly dashboard = inject(AdminDashboardService);
  private readonly settings  = inject(GlobalSettingsService);

  @ViewChildren('sparkCanvas') sparkCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;

  readonly kpis       = this.dashboard.kpis;
  readonly kpiLoading = this.dashboard.loading;
  readonly kpiError   = this.dashboard.loadError;

  readonly liveStatus  = signal<LiveSystemStatus>('optimal');
  readonly statusSaving = signal(false);

  readonly isMaintenance = computed(() => this.liveStatus() === 'maintenance');
  readonly statusLabel   = computed(() =>
    this.isMaintenance() ? 'Modo mantenimiento' : 'Sistema óptimo',
  );

  private charts: ChartInstance[] = [];

  async ngAfterViewInit(): Promise<void> {
    await this.#loadMaintenanceStatus();
    await this.dashboard.loadKpis();
    await this.#initSparklines();
  }

  ngOnDestroy(): void {
    for (const chart of this.charts) chart.destroy();
    this.charts = [];
  }

  async toggleLiveStatus(): Promise<void> {
    if (this.statusSaving()) return;
    const next: LiveSystemStatus = this.isMaintenance() ? 'optimal' : 'maintenance';
    this.statusSaving.set(true);
    try {
      await this.settings.saveMaintenanceMode(next === 'maintenance');
      this.liveStatus.set(next);
    } finally {
      this.statusSaving.set(false);
    }
  }

  async #loadMaintenanceStatus(): Promise<void> {
    try {
      const on = await this.settings.loadMaintenanceMode();
      this.liveStatus.set(on ? 'maintenance' : 'optimal');
    } catch {
      this.liveStatus.set('optimal');
    }
  }

  async #initSparklines(): Promise<void> {
    const { Chart, registerables } = await import('chart.js');
    Chart.register(...registerables);

    const cards    = this.kpis();
    const sparkEls = this.sparkCanvases.toArray();
    cards.forEach((card, index) => {
      const el = sparkEls[index]?.nativeElement;
      if (!el) return;
      this.charts.push(new Chart(el, sparklineConfig(card)));
    });
  }
}
